import { SimpleDialog } from '@app/hooks/use-simple-dialog';
import { pcfRoutes } from '@app/pcf/pcf-routes';
import {
  useDeleteProduct,
  useDuplicateProduct,
} from '@shared/api/hooks/pcf/products';
import { ProductResult } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { generatePath, useNavigate } from 'react-router-dom';

interface ProductDetailPageToolbarProps {
  product: ProductResult;
  canEdit?: boolean;
}

export default function ProductDetailPageToolbar({
  product,
  canEdit = true,
}: ProductDetailPageToolbarProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { mutateAsync: deleteProduct, isLoading: isDeleting } =
    useDeleteProduct();
  const { mutateAsync: duplicateProduct, isLoading: isDuplicating } =
    useDuplicateProduct();

  const editPage = generatePath(pcfRoutes.PRODUCT_EDIT, {
    product_id: product.uuid,
  });

  const handleOnDelete = async () => {
    try {
      await deleteProduct({ productUuid: product.uuid });
      toast({
        description: 'Product deleted',
      });
      navigate(pcfRoutes.PRODUCTS);
    } catch (error) {
      sentry.log(error);
    }
  };

  const onProductDuplicate = async () => {
    try {
      const duplicatedProduct = await duplicateProduct({
        productUuid: product.uuid,
      });
      toast({
        description: 'Product duplicated successfully.',
        variant: 'success',
      });
      navigate(
        generatePath(pcfRoutes.PRODUCT_EDIT, {
          product_id: duplicatedProduct.uuid,
        })
      );
    } catch (error) {
      sentry.log(error);
      toast({
        description: 'Failed to duplicate product.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex items-center gap-2">
      <SimpleTooltip
        disabled={canEdit}
        trigger={
          <Button
            variant="ghost"
            className="justify-start"
            onClick={() => navigate(editPage)}
            disabled={!canEdit}
          >
            Edit
          </Button>
        }
        content="Product managed via integration"
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="size-8 p-0">
            <span className="sr-only">Open menu</span>
            <Icons.ellipsis className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="flex flex-col">
          <Button
            variant="ghost"
            className="justify-start"
            onClick={() => onProductDuplicate()}
            loading={isDuplicating}
          >
            Duplicate
          </Button>
          <SimpleDialog
            trigger={
              <Button
                variant="ghost"
                className="justify-start text-destructive"
                disabled={!canEdit}
              >
                <Text
                  includeHover={!canEdit}
                  hoverText="Product managed via integration"
                >
                  Delete
                </Text>
              </Button>
            }
            header="Delete product"
            content={
              <p>
                Are you sure you want to delete this product?
                <p className="mt-2 font-bold">This action cannot be undone.</p>
              </p>
            }
            footer={
              <Button
                type="submit"
                onClick={async () => handleOnDelete()}
                loading={isDeleting}
                variant="destructive"
              >
                Delete
              </Button>
            }
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
